/**
 * The three-way archive choice (#191, decided with Samuel 2026-09-22).
 *
 * Shown ONLY when something is under way on the cohort — a Running or
 * Paused group run, or someone mid-orientation outside one. The plain
 * case keeps its one-line confirm: a `window.confirm` cannot express
 * "end their sessions" versus "let them finish", and archiving used to
 * silently mean the first.
 *
 * Presentation only; the page owns both reducers.
 */

export function ArchiveCohortDialog({
  cohortName,
  liveRuns,
  soloInProgress,
  busy,
  onCancel,
  onArchiveEndSessions,
  onArchiveFinishLive,
}: {
  cohortName: string;
  liveRuns: number;
  soloInProgress: number;
  busy: boolean;
  onCancel: () => void;
  /** `archive_cohort` — the destructive one. */
  onArchiveEndSessions: () => void;
  /** `archive_cohort_finish_live` — keeps live rooms and started runs. */
  onArchiveFinishLive: () => void;
}) {
  const total = liveRuns + soloInProgress;
  const parts = [
    liveRuns > 0 ? `${liveRuns} group${liveRuns === 1 ? '' : 's'}` : null,
    soloInProgress > 0
      ? `${soloInProgress} on their own`
      : null,
  ].filter(Boolean) as string[];

  return (
    <>
      <div
        onClick={busy ? undefined : onCancel}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 50 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Archive ${cohortName}`}
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 460,
          maxWidth: '92vw',
          background: 'var(--panel)',
          color: 'var(--text)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--r, 8px)',
          boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
          zIndex: 51,
          padding: 18,
          display: 'grid',
          gap: 14,
        }}
      >
        <div>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            “{cohortName}” still has {total} orientation{total === 1 ? '' : 's'} in progress.
          </div>
          {parts.length > 0 ? (
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
              {parts.join(' · ')}
            </div>
          ) : null}
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
            Archiving freezes the roster either way. It never touches finished
            orientations or the review queue.
          </div>
        </div>

        <div style={{ display: 'grid', gap: 10 }}>
          {/* Default focus: the non-destructive way out. */}
          <Choice
            label="Keep cohort"
            detail="Nothing changes."
            busy={busy}
            autoFocus
            onClick={onCancel}
          />
          <Choice
            label="Archive, let them finish"
            detail="Running and paused groups keep their room and leader, started orientations continue. Lobbies nobody started and not-started members are cleared. You can still cancel the rest from Live Sessions."
            busy={busy}
            onClick={onArchiveFinishLive}
          />
          <Choice
            label="Archive and end sessions"
            detail="Group rooms close; anyone mid-orientation carries on alone in a session of their own; members who never started are unassigned."
            busy={busy}
            danger
            onClick={onArchiveEndSessions}
          />
        </div>
      </div>
    </>
  );
}

function Choice({
  label,
  detail,
  busy,
  danger,
  autoFocus,
  onClick,
}: {
  label: string;
  detail: string;
  busy: boolean;
  danger?: boolean;
  autoFocus?: boolean;
  onClick: () => void;
}) {
  return (
    <div style={{ display: 'grid', gap: 3 }}>
      <button
        type="button"
        className={'btn ' + (danger ? 'danger' : '')}
        disabled={busy}
        autoFocus={autoFocus}
        onClick={onClick}
        style={{ justifyContent: 'center' }}
      >
        {label}
      </button>
      <span style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.45 }}>{detail}</span>
    </div>
  );
}
